<script lang="ts">
    import { STATUS } from "@/constants/statuses";
    import WishlistItemOptions from "@apps/wishlist/components/WishlistItemOptions.svelte";
    import WishlistTheme from "@apps/wishlist/components/WishlistTheme/WishlistTheme.svelte";
    import {
        DESIGN_MODE_SHARE_TOKEN,
        SAVE_WISHLIST_MODAL_ACTION_TYPES,
    } from "@apps/wishlist/constants/constants";
    import { SaveWishlistModalWidgetDefinition } from "@apps/wishlist/definitions";
    import { ShareWishlistsQuery } from "@apps/wishlist/queries/ShareWishlistsQuery";
    import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
    import WishlistsBackInStockItemsQuery from "@apps/wishlist/queries/WishlistsBackInStockItemsQuery";
    import { ShareWishlistsService } from "@apps/wishlist/services/ShareWishlistsService";
    import WishlistGuestInfoService from "@apps/wishlist/services/WishlistGuestInfoService";
    import { WishlistProductStatusesService } from "@apps/wishlist/services/WishlistProductStatusesService";
    import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
    import WishlistsStorefrontValuesService from "@apps/wishlist/services/WishlistsStorefrontValuesService";
    import { saveWishlistModalToken } from "@apps/wishlist/tokens";
    import { WISHLIST_ITEMS_SORTING_TYPE } from "@apps/wishlist/types";
    import { setTypedContext } from "@lib/svelte-typed-context";
    import { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
    import { ROLE } from "@modules/authentication/constants";
    import { CartQuery } from "@modules/cart/queries/CartQuery";
    import { CartService } from "@modules/cart/services/CartService";
    import CartAvailabilityProvider from "@modules/common_module/CartAvailabilityProvider/CartAvailabilityProvider";
    import { DesignModeProviderInterface } from "@modules/common_module/DesignModeProvider/DesignModeProviderInterface";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import { CombinedProductsAndProductsWithQuantityRulesQuery } from "@modules/products/queries/CombinedProductsAndProductsWithQuantityRulesQuery";
    import { ProductsQuery } from "@modules/products/queries/ProductsQuery";
    import { ProductsWithQuantityRulesQuery } from "@modules/products/queries/ProductsWithQuantityRulesQuery";
    import { lastValueFrom, map } from "rxjs";
    import { onMount } from "svelte";

    import { ModalManager } from "@components/Modal/ModalManager";
    import T from "@components/T.svelte";

    import WishlistPageItem from "../wishlist_page_widget/components/WishlistPageItem.svelte";
    import WishlistPageList from "../wishlist_page_widget/components/WishlistPageList.svelte";
    import WishlistPageListSkeleton from "../wishlist_page_widget/components/WishlistPageListSkeleton.svelte";
    import { wishlistPageWidgetContext } from "../wishlist_page_widget/WishlistPageWidgetContext";
    import { OpenedWishlistsUIStore } from "../wishlists_widget/ui_stores/OpenedWishlistsUIStore";

    export let shareWishlistsService: ShareWishlistsService;
    export let shareWishlistsQuery: ShareWishlistsQuery;
    export let wishlistsService: WishlistsService;
    export let wishlistsBackInStockItemsQuery: WishlistsBackInStockItemsQuery;
    export let wishlistProductStatusesService: WishlistProductStatusesService;
    export let wishlistProductStatusesQuery: WishlistProductStatusesQuery;
    export let productsQuery: ProductsQuery;
    export let combinedProductsAndProductsWithQuantityRulesQuery: CombinedProductsAndProductsWithQuantityRulesQuery;
    export let productsWithQuantityRulesQuery: ProductsWithQuantityRulesQuery;
    export let cartService: CartService;
    export let cartQuery: CartQuery;
    export let dynamicImportManager: DynamicImportManager;
    export let cartAvailabilityProvider: CartAvailabilityProvider;
    export let designModeProvider: DesignModeProviderInterface;
    export let wishlistsStorefrontValuesService: WishlistsStorefrontValuesService;
    export let authenticationInfoProvider: AuthenticationInfoProviderInterface;
    export let wishlistGuestInfoService: WishlistGuestInfoService;
    export let modalManager: ModalManager;
    export let afterWidgetInject: () => void;

    const wishlistBranding$ =
        wishlistsStorefrontValuesService.selectWishlistsBranding();
    const wishlistSettings$ =
        wishlistsStorefrontValuesService.selectWishlistsSettings();

    const isCustomer$ = authenticationInfoProvider
        .selectRole()
        .pipe(map((role) => role === ROLE.Customer));

    const openedWishlistsUIStore = new OpenedWishlistsUIStore();

    const status$ = shareWishlistsQuery.status$;
    const sharedwishlists$ = shareWishlistsQuery.selectAll();
    const loadMoreStatus$ = shareWishlistsQuery.loadMoreStatus$;
    const isCartAvailable = cartAvailabilityProvider.isCartAvailable();
    const isDesignMode = designModeProvider.isDesignMode();

    setTypedContext(wishlistPageWidgetContext, { cartService, cartQuery });

    onMount(() => {
        const urlSearchParams = new URLSearchParams(window.location.search);
        const shareToken = urlSearchParams.get("shareToken");

        if (isDesignMode)
            return lastValueFrom(
                shareWishlistsService.getSharedWishlists(
                    DESIGN_MODE_SHARE_TOKEN
                )
            );

        if (shareToken) {
            lastValueFrom(shareWishlistsService.getSharedWishlists(shareToken));
        }
        lastValueFrom(wishlistsService.getWishlists());

        const sharedWishlistsSubscribe = sharedwishlists$.subscribe(
            (sharedWishlists) => {
                sharedWishlists.forEach((sharedWishlist) => {
                    sharedWishlist.items.forEach((item) => {
                        if (item.productId) {
                            wishlistProductStatusesService.addProductIdForFetching(
                                item.productId
                            );
                        }
                    });
                });
            }
        );

        afterWidgetInject();

        return () => sharedWishlistsSubscribe.unsubscribe();
    });

    const handleLoadMore = (e: CustomEvent) => {
        lastValueFrom(shareWishlistsService.getWishlistItems(e.detail));
    };

    const handleOpenSaveModal = async (
        productId: number,
        variantId: number
    ) => {
        await dynamicImportManager.fetch({
            definition: SaveWishlistModalWidgetDefinition,
        });
        // Ensure the modal widget initializes asynchronously before opening it.
        setTimeout(() => {
            modalManager.openModal(saveWishlistModalToken, undefined, {
                actionType: SAVE_WISHLIST_MODAL_ACTION_TYPES.addItem,
                productId: productId,
                variantId: variantId,
            });
        }, 0);
    };

    const handleAddItem = (productId: number, variantId: number) => {
        if (!$wishlistSettings$) return;

        const addItem = () =>
            lastValueFrom(
                wishlistsService.addItem({
                    productId,
                    variantId,
                    wishlistId: null,
                })
            );

        if ($wishlistSettings$.isDisabledForGuests && !$isCustomer$) {
            const isSaved = wishlistGuestInfoService.isCurrentGuestInfoSaved();
            if (isSaved) {
                addItem();
            } else {
                handleOpenSaveModal(productId, variantId);
            }
        } else {
            addItem();
        }
    };

    const handleDeleteItem = (productId: number, variantId: number) => {
        lastValueFrom(
            wishlistsService.deleteItem({
                wishlistId: null,
                productId,
                variantId,
            })
        );
    };
</script>

{#if $wishlistBranding$}
    <WishlistTheme branding={$wishlistBranding$}>
        <div class="gw-wl-shared-page__wrapper">
            <div class="gw-wl-shared-page">
                <div class="gw-wl-shared-page__header">
                    {#if $sharedwishlists$ && $sharedwishlists$.length}
                        <h3 class="gw-wl-shared-page__header-title">
                            {#if $sharedwishlists$.length === 1}
                                <T
                                    namespace="wishlist.SharedWishlistPageWidget"
                                    translationKey="single_list_title"
                                />
                            {:else}
                                <T
                                    namespace="wishlist.SharedWishlistPageWidget"
                                    translationKey="plural_list_title"
                                />
                            {/if}
                        </h3>
                    {/if}
                </div>

                {#if [STATUS.INITIAL_LOADING, STATUS.LOADING].includes($status$) && $sharedwishlists$.length === 0}
                    <WishlistPageListSkeleton />
                {/if}

                {#each $sharedwishlists$ as sharedWishlist (sharedWishlist.id)}
                    <WishlistPageList
                        on:loadMore={handleLoadMore}
                        loadMoreStatus={$loadMoreStatus$}
                        wishlist={sharedWishlist}
                        {productsQuery}
                        {combinedProductsAndProductsWithQuantityRulesQuery}
                        {modalManager}
                        {dynamicImportManager}
                        currentSort={WISHLIST_ITEMS_SORTING_TYPE.recentlyAdded}
                        {openedWishlistsUIStore}
                        isSharedList
                        criticalNamespace="wishlist.critical.SharedWishlistPageWidget"
                        namespace="wishlist.SharedWishlistPageWidget"
                        let:filteredWishlistItems
                    >
                        {#each filteredWishlistItems as item (new Set( [item.productId, item.variantId] ))}
                            <WishlistPageItem
                                {item}
                                wishlists={$sharedwishlists$}
                                {productsQuery}
                                {combinedProductsAndProductsWithQuantityRulesQuery}
                                {productsWithQuantityRulesQuery}
                                {wishlistsBackInStockItemsQuery}
                                {wishlistProductStatusesQuery}
                                isSharedItem
                                criticalNamespace="wishlist.critical.SharedWishlistPageWidget"
                                namespace="wishlist.SharedWishlistPageWidget"
                                {dynamicImportManager}
                                {isCartAvailable}
                                on:addItem={() =>
                                    handleAddItem(
                                        item.productId,
                                        item.variantId
                                    )}
                                on:deleteItem={() =>
                                    handleDeleteItem(
                                        item.productId,
                                        item.variantId
                                    )}
                                canEditWishlist={true}
                                isEditProductQuantityEnabled={false}
                            >
                                <svelte:fragment
                                    slot="options"
                                    let:selectedOptions
                                >
                                    <WishlistItemOptions {selectedOptions} />
                                </svelte:fragment>
                            </WishlistPageItem>
                        {/each}
                    </WishlistPageList>
                {/each}
            </div>
        </div>
    </WishlistTheme>
{/if}

<style lang="postcss">
    .gw-wl-shared-page {
        max-width: 1240px;
        margin: 0 auto;
        word-break: normal;

        &__wrapper {
            margin-left: calc(-50vw + 50%);
            margin-right: calc(-50vw + 50%);
            padding: 0 40px;

            @media all and (max-width: 700px) {
                margin-right: 0;
                margin-left: 0;
                padding: 0 20px;
            }

            @media all and (max-width: 500px) {
                padding: 0;
            }
        }

        @media all and (max-width: 960px) {
            padding: 0 0.9em;
        }

        & :global(.gw-btn.gw-button) {
            border-radius: calc(
                1px *
                    var(
                        --gw-wl-wishlistDrawerAndPageSettings-buttonsBorderRadius
                    )
            ) !important;
        }

        & :global(.gw-btn.gw-button.--contained) {
            background-color: var(
                --gw-wl-wishlistDrawerAndPageSettings-buttonsColor
            );
            color: var(--gw-wl-wishlistDrawerAndPageSettings-buttonsFontColor);
            transition: all 0.3s;
            border: 0;
        }

        & :global(.gw-btn.gw-button.--outlined) {
            background-color: transparent;
            box-shadow: 0 0 0 1px
                var(--gw-wl-wishlistDrawerAndPageSettings-buttonsColor);
            color: var(--gw-wl-wishlistDrawerAndPageSettings-buttonsFontColor);
            transition: all 0.3s;
            border: 0;
        }
    }
    .gw-wl-shared-page__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 0 1.25rem 0;
    }
    .gw-wl-shared-page__header-title {
        font-size: 22px;
        margin: 0 1.6rem 0 0;
        font-weight: 600;
    }
</style>
